import S from 's-js';
import { Role } from 'data/models/Role';
import { PCRoles } from 'data/models/PCRoles';
import { PilotRoles } from 'data/models/PilotRoles';

// roles that are soft implied by virtue of having another role
const implies = {} as { [key : number] : number[] };
// TH: No implied roles currently.

// roles that are hard materialized by virtue of having another role
const materializes = {} as { [key : number] : number[] };
materializes[PCRoles.ManageBerths] = [PCRoles.ReserveBerths];

/*
    * RoleMapper class.
    * 
    * A utility for mapping materialized/implied roles. Represents the roles possessed by an
    * individual user (so you construct one of these per user). Understands two kinds of
    * inference btwn roles:
    * 
    *   implies -- role A implies role B if possessing A enables all of the functional effects
    *              of possessing B, whether or not a row for B exists for this user in the Db.
    *              I.e., implication is checked in the UserRights layer, not materialized in the
    *              Db.
    * 
    *   materializes -- role A materializes role B if every time we add role B to a given user
    *                   we should also add role B. This means a row for each role should be
    *                   created for this user in the Db.
    *                   I.e. materialization is materialized in the Db and NOT checked in the
    *                   UserRights layer.
    * 
    * 
    * Constructor
    * 
    *   RoleMapper(assignedRoles, roleIdsByName)
    * 
    *     assignedRoles -- an observable array of role ids assigned to a particular user.
    *                      Corresponds to the roles actually materialized in Db for that user.
    *                      Can contain an initial set of roles possessed by the user.
    *                      Values in the array should be JS ints (not role objects or
    *                      observable ints).
    *                      Can be a KO observable array, or any array-valued observable (like
    *                      the result of calling observableArray.map())
    * 
    *     roleIdsByName -- a dictionary mapping camel-case role names (e.g. 'SiteAdmin', 'ManageUsers')
    *                      to the Db Ids for those roles. Property values can be plain JS ints
    *                      or observable ints.
    * 
    * Methods
    * 
    *   HasOrImplies(id) -- do the current assigned roles contain or imply the given role id?
    * 
    *     id -- the role id. A plain JS int or an observable int.
    * 
    *     returns -- a computed observable with a boolean value
    * 
    *   
    *   ImpliesOrMaterializes(id) -- do the current assigned roles imply or materialize the given
    *                                role
    *     
    *     id -- the role id. A plain JS int or an observable int.
    * 
    *     returns -- a compute observable with a boolean value
    * 
    *     NOTE: Tests STRICTLY whether any of the assigned roles implies or materializes the given
    *     role. Observable will evaluate to false if the assigned roles only contain the given role.
    * 
    * 
    *   ToggleRole(addRole, removeRole, id) -- produce a function that toggles the state of given role
    * 
    *     addRole -- a callback to add a role. Takes the int id of the role to be added.
    * 
    *     removeRole -- a callback to remove a role. Takes the int id of the role to be removed.
    * 
    *     id -- the role id. A plain JS int or an observable int.
    * 
    *     returns -- a function to be used with KO's click binding.
    * 
    *     NOTE: ToggleRole takes care of figuring out materializes and implies relationships so
    *     that addRole and removeRole are called for exactly the roles that need to be turned
    *     on and off.
    *     So your addRole and removeRole implementations just need the logic for
    *     turning on or off an individual role. E.g. should the change be written to the Db
    *     immediately, or should it be stored in an array to be written to the Db as a group
    *     later?
    */
export function RoleMapper(assignedRoles : () => number[]) {
    return {
        HasOrImpliesRole: function (id : number) {
            return assignedRoles().some(roleId => roleId === id || (implies[roleId] || []).indexOf(id) >= 0);
        },
        ImpliesOrMaterializesRole: function (id : number) {
            return assignedRoles().some(roleId => (implies[roleId] || []).indexOf(id) >= 0 || (materializes[roleId] || []).indexOf(id) >= 0);
        },
        ToggleRole: function (addRole : (role : number) => void, removeRole : (role : number) => void, id : number) {
            let _assigned = S.sample(assignedRoles),
                hasRole = _assigned.indexOf(id) >= 0,
                materializedOrSame = [id].concat(materializes[id] || []),
                impliedRoles = implies[id] || [];

            // removing a role removes all of its materialized roles
            // adding a role adds all of its materialized roles (provided they aren't already added)
            for (let i = 0; i < materializedOrSame.length; i++) {
                let role = materializedOrSame[i];
                if (hasRole) removeRole(role);
                else if (role === id || _assigned.indexOf(role) === -1) addRole(role);
            }

            // adding a role REMOVES all of its implied roles
            // removing a role has no effect on implied roles
            for (let i = 0; i < impliedRoles.length; i++) {
                let role = impliedRoles[i];
                if (!hasRole && _assigned.indexOf(role) >= 0) removeRole(role);
            }
        }
    }
}


// WEBPACK FOOTER //
// ./apps/common/services/rolemapper.ts